(() => {
  const FIT_KEY = 'faf:fit-preferences';
  const SIZE_KEYS = [
    'womensFootwearSize',
    'mensFootwearSize',
    'womensApparelNumeric',
    'womensApparelAlpha',
    'womensBottomsNumeric',
    'mensApparelAlpha',
  ];
  const observedViews = new WeakSet();

  let pendingSave = null;
  let meaningfulTracked = false;

  const readFit = () => {
    const resolved = window.FAFFitProfile?.read?.();

    if (resolved && typeof resolved === 'object') {
      return resolved;
    }

    try {
      const value = JSON.parse(
        window.localStorage.getItem(FIT_KEY) || '{}'
      );

      return value && typeof value === 'object' ? value : {};
    } catch (error) {
      return {};
    }
  };

  const publish = (name, data = {}) => {
    const eventProperties = {
      ...data,
      faf_schema_version: '2',
      country: window.FAFThemeConfig?.countryCode || '',
      page_type: window.FAFThemeConfig?.pageType || 'storefront',
      page_path: window.location.pathname,
    };

    const elevarEvent = `faf_${name}`;

    if (window.FAFAnalytics?.custom) {
      window.FAFAnalytics.custom(elevarEvent, eventProperties);
    } else {
      window.ElevarDataLayer = window.ElevarDataLayer ?? [];
      window.ElevarDataLayer.push({
        event: elevarEvent,
        event_properties: eventProperties,
      });
    }

    const analytics = window.Shopify?.analytics;
    if (!analytics?.publish) return;

    Promise.resolve(
      analytics.publish(`faf:${name}`, eventProperties)
    ).catch(() => {});
  };

  const markMeaningful = (action, context = {}) => {
    if (meaningfulTracked) return;

    meaningfulTracked = true;

    publish('fit_profile_meaningful_progression', {
      action,
      ...context,
    });
  };

  const pdpConfig = (section) => {
    const node = section?.querySelector(
      "script[id^='PdpFitConfig-']"
    );

    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch (error) {
      return {};
    }
  };

  const pdpProfileKey = (section) => {
    const alpineKey = String(
      window.Alpine?.$data?.(section)?.profileKey || ''
    ).trim();

    if (alpineKey) return alpineKey;

    return String(
      pdpConfig(section).profileKey || ''
    ).trim();
  };

  const checkedVariantId = (productId) => {
    if (!productId) return '';

    const checked = document.querySelector(
      `[data-variant-selector] input[data-pdp-product-id='${CSS.escape(String(productId))}']:checked`
    );

    return String(checked?.value || '');
  };

  const observeOnce = (node, callback) => {
    if (!node || observedViews.has(node)) return;

    observedViews.add(node);

    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        callback();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
  };

  const initViews = () => {
    document
      .querySelectorAll('[data-pdp-fit-suggest]')
      .forEach((section) => {
        observeOnce(section, () => {
          const config = pdpConfig(section);
          const profileKey = pdpProfileKey(section);

          if (!profileKey) return;

          const context = {
            surface: 'pdp',
            product_id: String(config.productId || ''),
            profile_key: profileKey,
          };

          publish('save_size_viewed', context);

          if (String(readFit()[profileKey] || '').trim()) {
            publish('saved_size_reused', context);
            markMeaningful('saved_size_reused', context);
          }
        });
      });

    document
      .querySelectorAll('.push-account-fit-preferences')
      .forEach((panel) => {
        observeOnce(panel, () => {
          publish('account_fit_viewed', {
            surface: 'account',
          });
        });
      });
  };

  const beginPdpSave = (button) => {
    const section = button.closest('[data-pdp-fit-suggest]');
    const config = pdpConfig(section);
    const profileKey = pdpProfileKey(section);

    if (!profileKey) return;

    const context = {
      surface: 'pdp',
      product_id: String(config.productId || ''),
      profile_key: profileKey,
      variant_id: checkedVariantId(config.productId),
    };

    pendingSave = {
      before: readFit(),
      keys: [profileKey],
      context,
    };

    publish('save_size_clicked', context);
  };

  const beginAccountSave = () => {
    pendingSave = {
      before: readFit(),
      keys: SIZE_KEYS,
      context: {
        surface: 'account',
      },
    };

    publish('save_size_clicked', {
      surface: 'account',
    });
  };

  const finishSave = () => {
    if (!pendingSave) return;

    const { before, keys, context } = pendingSave;
    pendingSave = null;

    const after = readFit();

    keys.forEach((key) => {
      const previous = String(before[key] || '').trim();
      const current = String(after[key] || '').trim();

      if (previous === current) return;

      const payload = {
        ...context,
        profile_key: key,
      };

      if (!previous && current) {
        publish('save_size_saved', payload);
        markMeaningful('size_saved', payload);
        return;
      }

      if (previous && !current) {
        publish('save_size_removed', payload);
        return;
      }

      publish('save_size_updated', payload);
      markMeaningful('size_updated', payload);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element ? event.target : null;

      const button = target?.closest('button');
      if (!button) return;

      const pdpSection = button.closest('[data-pdp-fit-suggest]');
      const pdpAction = button.getAttribute('x-on:click.prevent');

      if (pdpSection && pdpAction === 'saveSelection()') {
        beginPdpSave(button);
        return;
      }

      const accountPanel = button.closest(
        '.push-account-fit-preferences'
      );

      if (
        accountPanel &&
        button.getAttribute('aria-controls') ===
          'push-account-fit-fields' &&
        button.getAttribute('aria-expanded') !== 'true'
      ) {
        publish('account_fit_opened', {
          surface: 'account',
        });
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form =
        event.target instanceof HTMLFormElement
          ? event.target
          : null;

      if (form?.matches('.push-account-fit-preferences form')) {
        beginAccountSave();
      }
    },
    true
  );

  window.addEventListener('faf:fit-profile-changed', finishSave);
  window.addEventListener('lazy-panel:ready', initViews);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initViews,
      { once: true }
    );
  } else {
    initViews();
  }
})();